import {
  QueryClient,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { SnapshotImage } from 'src/types';
import { generateKey } from 'src/util/generateKey';
import { preserveAwsUrl } from 'src/util/preserveAwsUrl';
import { createScopedMutation } from '../createScopedMutation';
import { DELETE_KEY, IMAGE_QUERY_KEY, IMAGE_UPLOAD_MUTATION_KEY, LIST_KEY, POST_KEY, PUT_KEY } from '../queryKeys';
import {
  invalidateIfAllMutationsDone,
  JSON_MUTATION_TIMEOUT_MS,
  queryYemboApiCall,
  queryYemboApiCallWithReturnValue,
  UPLOAD_TIMEOUT_MS,
} from '../queryYemboApiCalls';
import { useIsReadyForFetch } from '../useIsReadyForFetch';

const url = 'image';

type PartialImage = Partial<SnapshotImage> & Pick<SnapshotImage, 'key'>;

export const generateDummyImage = ({ key, videoKey }: { key?: string; videoKey: string }): SnapshotImage => ({
  key: key ?? generateKey({ type: 'image' }),
  videoKey,
  width: 0,
  height: 0,
  annotationSource: 'human',
  status: 'new',
  createdAt: new Date().toISOString(),
});

/**
 * Every photo on the job, straight from `GET /image?jobKey=`, bypassing the
 * cache. The cached list keeps each photo's first signed URL (preserveAwsUrl),
 * which expires after an hour; a report that downloads the photos needs URLs
 * signed now.
 */
export const fetchJobImages = (jobKey: string): Promise<SnapshotImage[]> => getImages(jobKey);

const getImages = async (jobKey: string): Promise<SnapshotImage[]> => {
  const images = await queryYemboApiCallWithReturnValue<SnapshotImage[], 'images'>({
    url,
    key: 'images',
    urlParams: new URLSearchParams({ jobKey }),
    shouldNotifyAndThrowError: true,
  });

  if (!images) throw new Error('Job not found');

  return images;
};

const imageToFormData = (image: PartialImage & { imageData?: Blob }) => {
  const form = new FormData();

  for (const [key, value] of Object.entries(image)) {
    if (value instanceof Blob) {
      form.append(key, value, `${image.key}.jpg`);
    } else if (value != null) {
      // skip null and undefined
      form.append(key, value.toString());
    }
  }

  return form;
};

export const postImage = (image: SnapshotImage, imageData?: Blob) =>
  queryYemboApiCall({
    url,
    method: 'POST',
    body: imageToFormData({ ...image, imageData }),
    shouldNotifyAndThrowError: true,
    timeoutMs: imageData ? UPLOAD_TIMEOUT_MS : JSON_MUTATION_TIMEOUT_MS,
  });

const putImage = (image: PartialImage) =>
  queryYemboApiCall({
    url,
    method: 'PUT',
    body: JSON.stringify(image),
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });

const deleteImage = (image: Pick<SnapshotImage, 'key'>) =>
  queryYemboApiCall({
    url,
    method: 'DELETE',
    body: JSON.stringify({ key: image.key }),
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });

type RotateImage = Pick<SnapshotImage, 'key'> & { degrees: number };

const putImageRotate = async (image: RotateImage) => {
  // FIXME: API returns image object under key `imageUrl` :-)
  const rotatedImage = await queryYemboApiCallWithReturnValue<SnapshotImage, 'imageUrl'>({
    url: `${url}/rotate`,
    key: 'imageUrl',
    method: 'PUT',
    body: JSON.stringify(image),
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });

  if (!rotatedImage) throw new Error('Image rotation failed');

  return rotatedImage;
};

export const postImageUploadRequest = async (companyKey: string) => {
  const result = await queryYemboApiCallWithReturnValue<Record<string, string>, 'formInputs'>({
    url: `${url}/upload-request`,
    method: 'POST',
    key: 'formInputs',
    body: JSON.stringify({ companyKey }),
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });

  if (!result) throw new Error('Image upload request failed');

  return result;
};

export const putImageContentType = async (params: { s3Key: string; contentType: string }) =>
  await queryYemboApiCall({
    url: `${url}/content-type`,
    method: 'PUT',
    body: JSON.stringify(params),
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });

export const uploadImageBlob = (imageKey: string, blob: Blob, timeoutMs = UPLOAD_TIMEOUT_MS) => {
  const form = new FormData();
  form.set('key', imageKey);
  form.append('file', blob, `${imageKey}.jpg`);

  return queryYemboApiCall({
    url: `${url}/upload`,
    method: 'POST',
    body: form,
    shouldNotifyAndThrowError: true,
    timeoutMs,
  });
};

type UseImagesOptions = Partial<UseQueryOptions<SnapshotImage[], unknown>>;

const revokeObjectURLStatic = (url: string) => URL.revokeObjectURL(url);

const useImagesQuery = (jobKey: string | undefined, options?: UseImagesOptions) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [IMAGE_QUERY_KEY, LIST_KEY, jobKey],
    queryFn: async () => {
      const images = await getImages(jobKey ?? '');
      const prevImages = queryClient.getQueryData<SnapshotImage[]>([IMAGE_QUERY_KEY, LIST_KEY, jobKey]);

      if (!prevImages) return images;

      // Use previous images AWS signed URLs to avoid re-uploading them that causes unnecessary network usage and delays
      return images.map((newImage) => {
        const prevImage = prevImages.find(({ key }) => key === newImage.key);
        return {
          ...newImage,
          url: preserveAwsUrl(prevImage?.url, newImage.url, revokeObjectURLStatic),
          importedUrl: preserveAwsUrl(prevImage?.importedUrl, newImage.importedUrl, revokeObjectURLStatic),
        };
      });
    },
    ...options,
    enabled: !!jobKey && options?.enabled !== false,
  });
};

export const useImagesQueryForOffline = (jobKey: string | undefined, options?: UseImagesOptions) =>
  useImagesQuery(jobKey, {
    ...options,
    enabled: useIsReadyForFetch() && options?.enabled !== false,
  });

type CreateImageOptions = UseMutationOptions<
  void,
  unknown,
  {
    image: SnapshotImage;
    isExistingImage: boolean;
    imageBlobToUpload?: Blob;
    recordingStartedAt?: string;
    shouldSendStubUrlToApi?: boolean;
  },
  void
>;

const getCreateImageOptions = (
  queryClient: QueryClient,
  roomKey: string,
  jobKey: string,
  options?: CreateImageOptions
): CreateImageOptions => {
  const mutationKey = [IMAGE_QUERY_KEY, jobKey, POST_KEY];
  const listQueryKey = [IMAGE_QUERY_KEY, LIST_KEY, jobKey];

  return {
    mutationKey,
    scope: { id: roomKey },
    mutationFn: async ({ image, isExistingImage, imageBlobToUpload, recordingStartedAt, shouldSendStubUrlToApi }) => {
      const blobUrlReplacement = shouldSendStubUrlToApi ? '_stub_url_' : null;

      const imageWithoutBlobs = {
        ...image,
        // If recordingStartedAt is provided, use it to override videoTimestamp on the backend
        ...(recordingStartedAt ? { recordingStartedAt } : undefined),
        ...(image.importedUrl?.startsWith('blob:') ? { importedUrl: blobUrlReplacement } : undefined),
        ...(image.url?.startsWith('blob:') ? { url: blobUrlReplacement } : undefined),
      };

      await (isExistingImage ? putImage(imageWithoutBlobs) : postImage(imageWithoutBlobs, imageBlobToUpload));

      if (isExistingImage && imageBlobToUpload) {
        // Upload image in a separate request in case of PUT
        await uploadImageBlob(image.key, imageBlobToUpload);
      }
    },
    ...options,
    onMutate: async (variables, context) => {
      const { image } = variables;

      await queryClient.cancelQueries({ queryKey: listQueryKey });

      queryClient.setQueryData<SnapshotImage[]>(listQueryKey, (previousImages) => {
        if (!previousImages) return undefined;
        return [...previousImages.filter(({ key }) => key !== image.key), image];
      });

      options?.onMutate?.(variables, context);
    },
    onSettled: (response, error, variables, onMutateResult, context) => {
      invalidateIfAllMutationsDone(listQueryKey, [IMAGE_QUERY_KEY, jobKey]);

      options?.onSettled?.(response, error, variables, onMutateResult, context);
    },
  };
};

export const { useScopedMutation: useCreateImage, useScopedMutationBuilder: useCreateImageBuilder } =
  createScopedMutation(getCreateImageOptions);

type PutImageOptions = UseMutationOptions<Response, unknown, PartialImage, void>;

export const usePutImage = (roomKey: string, jobKey: string, options?: PutImageOptions) => {
  const queryClient = useQueryClient();

  const mutationKey = [IMAGE_QUERY_KEY, jobKey, PUT_KEY];
  const listQueryKey = [IMAGE_QUERY_KEY, LIST_KEY, jobKey];

  return useMutation({
    mutationKey,
    scope: { id: roomKey },
    mutationFn: putImage,
    ...options,
    onMutate: async (image, context) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey });

      queryClient.setQueryData<SnapshotImage[]>(listQueryKey, (previousImages) =>
        previousImages?.map((previousImage) =>
          previousImage.key === image.key ? { ...previousImage, ...image } : previousImage
        )
      );

      options?.onMutate?.(image, context);
    },
    onSettled: (...variables) => {
      invalidateIfAllMutationsDone(listQueryKey, [IMAGE_QUERY_KEY, jobKey]);

      options?.onSettled?.(...variables);
    },
  });
};

type PutImageBlobOptions = UseMutationOptions<
  Response,
  unknown,
  Pick<SnapshotImage, 'key'> & { imageBlobToUpload: Blob },
  void
>;

export const usePutImageBlob = (jobKey: string, options?: PutImageBlobOptions & { timeoutMs?: number }) => {
  const queryClient = useQueryClient();

  const mutationKey = [IMAGE_QUERY_KEY, jobKey, PUT_KEY, IMAGE_UPLOAD_MUTATION_KEY];
  const listQueryKey = [IMAGE_QUERY_KEY, LIST_KEY, jobKey];

  const { timeoutMs, ...restOptions } = options ?? {};

  return useMutation({
    mutationKey,
    mutationFn: ({ key, imageBlobToUpload }) => uploadImageBlob(key, imageBlobToUpload, timeoutMs),
    ...restOptions,
    onMutate: async (image, context) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey });

      queryClient.setQueryData<SnapshotImage[]>(listQueryKey, (previousImages) =>
        previousImages?.map((previousImage) =>
          previousImage.key === image.key
            ? {
                ...previousImage,
                importedUrl: URL.createObjectURL(image.imageBlobToUpload),
              }
            : previousImage
        )
      );

      options?.onMutate?.(image, context);
    },
    onSettled: (response, error, image, onMutateResult, context) => {
      invalidateIfAllMutationsDone(listQueryKey, [IMAGE_QUERY_KEY, jobKey]);

      options?.onSettled?.(response, error, image, onMutateResult, context);
    },
  });
};

type PutImageRotateOptions = UseMutationOptions<SnapshotImage, unknown, RotateImage, void>;

export const usePutImageRotate = (roomKey: string, jobKey: string, options?: PutImageRotateOptions) => {
  const queryClient = useQueryClient();

  const mutationKey = [IMAGE_QUERY_KEY, jobKey, PUT_KEY];
  const listQueryKey = [IMAGE_QUERY_KEY, LIST_KEY, jobKey];

  return useMutation({
    mutationKey,
    scope: { id: roomKey },
    mutationFn: putImageRotate,
    ...options,
    onSuccess: async (responseImage, variables, onMutateResult, context) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey });

      queryClient.setQueryData<SnapshotImage[]>(listQueryKey, (previousImages) =>
        previousImages?.map((previousImage) =>
          previousImage.key === responseImage.key ? responseImage : previousImage
        )
      );

      options?.onSuccess?.(responseImage, variables, onMutateResult, context);
    },
    onSettled: (response, error, image, onMutateResult, context) => {
      invalidateIfAllMutationsDone(listQueryKey, [IMAGE_QUERY_KEY, jobKey]);

      options?.onSettled?.(response, error, image, onMutateResult, context);
    },
  });
};

type DeleteImageOptions = UseMutationOptions<Response, unknown, Pick<SnapshotImage, 'key'>, void>;

export const useDeleteImage = (roomKey: string, jobKey: string, options?: DeleteImageOptions) => {
  const queryClient = useQueryClient();

  const mutationKey = [IMAGE_QUERY_KEY, jobKey, DELETE_KEY];
  const listQueryKey = [IMAGE_QUERY_KEY, LIST_KEY, jobKey];

  return useMutation({
    mutationKey,
    scope: { id: roomKey },
    mutationFn: deleteImage,
    ...options,
    onMutate: async (variables, context) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey });

      queryClient.setQueryData<SnapshotImage[]>(listQueryKey, (previousImages) => {
        const deletedImage = previousImages?.find(({ key }) => key === variables.key);
        if (deletedImage?.url?.startsWith('blob:')) URL.revokeObjectURL(deletedImage.url);
        if (deletedImage?.importedUrl?.startsWith('blob:')) URL.revokeObjectURL(deletedImage.importedUrl);

        return previousImages?.filter(({ key }) => key !== variables.key);
      });

      options?.onMutate?.(variables, context);
    },
    onSettled: (response, error, image, onMutateResult, context) => {
      invalidateIfAllMutationsDone(listQueryKey, [IMAGE_QUERY_KEY, jobKey]);

      options?.onSettled?.(response, error, image, onMutateResult, context);
    },
  });
};
